import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Navigation, Route, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { MapPanel } from "../components/MapPanel";
import { StatCard } from "../components/StatCard";
import HandoverPanel from "../components/HandoverPanel";
import { formatDateTime, getStatusLabel } from "../lib/utils";

const checklistDefaults = {
  vehicle_safe: false,
  child_briefing: false,
  route_reviewed: false,
  safeguarding_ready: false,
};

export default function DriverDashboard() {
  const navigate = useNavigate();
  const { overview, trips, vehicles, loadOverview, loadTrips, loadVehicles, startTrip, completeTrip, sendLocationUpdate } = useData();
  const [checklist, setChecklist] = useState(checklistDefaults);
  const [handoverNotes, setHandoverNotes] = useState("");
  const [gpsState, setGpsState] = useState("idle");
  const lastSendRef = useRef(0);

  useEffect(() => {
    const hydrate = async () => {
      await Promise.all([loadOverview(), loadTrips(), loadVehicles()]);
    };
    hydrate();
    const interval = window.setInterval(hydrate, 10000);
    return () => window.clearInterval(interval);
  }, [loadOverview, loadTrips, loadVehicles]);

  const activeTrip = useMemo(
    () => trips.find((trip) => trip.status === "in_progress") || trips.find((trip) => ["assigned", "pending"].includes(trip.status)) || null,
    [trips]
  );

  const assignedVehicle = vehicles[0] || overview?.vehicles?.[0] || null;
  const checklistComplete = Object.values(checklist).every(Boolean);

  useEffect(() => {
    if (!activeTrip || activeTrip.status !== "in_progress") {
      setGpsState("idle");
      return undefined;
    }

    if (!navigator.geolocation) {
      setGpsState("unsupported");
      return undefined;
    }

    const watchId = navigator.geolocation.watchPosition(
      async ({ coords }) => {
        const now = Date.now();
        if (now - lastSendRef.current < 10000) return;
        lastSendRef.current = now;
        setGpsState("syncing");
        const result = await sendLocationUpdate({
          trip_id: activeTrip.id,
          latitude: coords.latitude,
          longitude: coords.longitude,
          speed: coords.speed ? Number((coords.speed * 3.6).toFixed(1)) : null,
          timestamp: new Date().toISOString(),
        });
        if (result.success) {
          setGpsState("live");
          loadTrips();
        } else {
          setGpsState("error");
        }
      },
      () => {
        setGpsState("permission_denied");
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [activeTrip, sendLocationUpdate, loadTrips]);

  const handleStartTrip = async () => {
    if (!activeTrip) return;
    if (!checklistComplete) {
      toast.error("Complete the pre-journey checklist before starting the trip.");
      return;
    }
    const result = await startTrip(activeTrip.id, { prejourney_completed: true });
    if (result.success) {
      toast.success("Trip started. GPS tracking is now active.");
    } else {
      toast.error(result.error);
    }
  };

  const handleCompleteTrip = async () => {
    if (!activeTrip) return;
    if (!activeTrip.handover_verified_at) {
      toast.error("Record handover verification before completing the trip.");
      return;
    }
    const result = await completeTrip(activeTrip.id, { handover_notes: handoverNotes });
    if (result.success) {
      toast.success("Trip completed and handover recorded.");
      setHandoverNotes("");
      setChecklist(checklistDefaults);
    } else {
      toast.error(result.error);
    }
  };

  const markers = activeTrip?.last_location ? [activeTrip.last_location] : [];

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Assigned trips" value={overview?.stats?.assigned_trips || 0} icon={Route} accent="blue" helper="Trips linked to your driver account" />
        <StatCard label="Active trips" value={overview?.stats?.active_trips || 0} icon={Navigation} accent="emerald" helper="GPS updates only run while a trip is in progress" />
        <StatCard label="Vehicle" value={assignedVehicle?.registration || "Pending"} icon={ShieldCheck} accent="amber" helper="Assigned vehicle for your current shift" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <div className="space-y-6">
          <div className="guardian-card bg-white p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Pre-journey checklist</h2>
                <p className="mt-1 text-sm text-[#64748B]">Drivers must confirm readiness before the trip moves to in-progress.</p>
              </div>
              <span className="rounded-full bg-[#E8EEF6] px-3 py-1 text-xs font-semibold text-[#163D6B]">
                {Object.values(checklist).filter(Boolean).length}/4 complete
              </span>
            </div>
            <div className="mt-5 grid gap-3">
              {[
                ["vehicle_safe", "Vehicle checks complete and road-ready"],
                ["child_briefing", "Child notes and safeguarding details reviewed"],
                ["route_reviewed", "Pickup and destination route reviewed"],
                ["safeguarding_ready", "Handover and emergency plan confirmed"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 text-sm text-[#0F172A]">
                  <input
                    type="checkbox"
                    checked={checklist[key]}
                    onChange={(event) => setChecklist((current) => ({ ...current, [key]: event.target.checked }))}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={handleStartTrip}
                disabled={!activeTrip || activeTrip.status === "in_progress"}
                className="rounded-xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#0E2E53] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Start trip
              </button>
              <button
                onClick={() => navigate("/driver/incidents")}
                className="rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm font-semibold text-[#0F172A]"
              >
                Report incident
              </button>
            </div>
          </div>

          <div className="guardian-card bg-white p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Live GPS status</h2>
                <p className="mt-1 text-sm text-[#64748B]">Location updates send every 5–10 seconds while a trip is active.</p>
              </div>
              <span className="rounded-full bg-[#F8FAFC] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#163D6B]">
                {gpsState.replace(/_/g, " ")}
              </span>
            </div>
            <div className="mt-5 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#475569]">
              {gpsState === "unsupported" && "This device does not support browser geolocation."}
              {gpsState === "permission_denied" && "GPS permission was denied. Enable location access to transmit live tracking."}
              {gpsState === "syncing" && "Sending your latest GPS coordinates…"}
              {gpsState === "live" && "GPS tracking is active and updating the trip location feed."}
              {gpsState === "idle" && "GPS tracking will activate when a trip enters the in-progress state."}
              {gpsState === "error" && "Location updates failed. Recheck connectivity or try again."}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="guardian-card bg-white p-6">
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Current trip</h2>
            {activeTrip ? (
              <div className="mt-4 space-y-4">
                <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                  <p className="font-semibold text-[#0F172A]">{activeTrip.child_name}</p>
                  <p className="mt-1 text-sm text-[#64748B]">{activeTrip.pickup_location}</p>
                  <p className="text-sm text-[#64748B]">to {activeTrip.dropoff_location}</p>
                  <p className="mt-3 text-sm font-medium text-[#163D6B]">Status: {getStatusLabel(activeTrip.status)}</p>
                  <p className="mt-1 text-xs text-[#64748B]">Last updated {formatDateTime(activeTrip.last_location?.timestamp || activeTrip.updated_at)}</p>
                </div>
                <MapPanel markers={markers} height={280} title="Driver live location" />
                <textarea
                  value={handoverNotes}
                  onChange={(event) => setHandoverNotes(event.target.value)}
                  placeholder="Add handover confirmation notes before completing the trip"
                  className="min-h-[110px] w-full rounded-2xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm outline-none focus:border-[#163D6B]"
                />
                <button
                  onClick={handleCompleteTrip}
                  disabled={activeTrip.status !== "in_progress" || !activeTrip.handover_verified_at}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <CheckCircle2 size={16} />
                  Complete handover
                </button>
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-dashed border-[#CBD5E1] p-4 text-sm text-[#64748B]">No trip is currently assigned to this driver account.</div>
            )}
          </div>

          {activeTrip && (
            <HandoverPanel trip={activeTrip} role="driver" onRefresh={loadTrips} />
          )}

          <div className="guardian-card bg-white p-6">
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Assigned trip queue</h2>
            <div className="mt-4 space-y-3">
              {trips.length ? (
                trips.map((trip) => (
                  <div key={trip.id} className="rounded-2xl border border-[#E2E8F0] p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#0F172A]">{trip.child_name}</p>
                        <p className="text-sm text-[#64748B]">{trip.pickup_location}</p>
                      </div>
                      <span className="rounded-full bg-[#F8FAFC] px-3 py-1 text-xs font-semibold text-[#163D6B]">{getStatusLabel(trip.status)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-[#CBD5E1] p-4 text-sm text-[#64748B]">No driver trips loaded.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
